import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  faFacebookF,
  faInstagram,
  faYoutube,
  faTwitter,
} from '@fortawesome/free-brands-svg-icons';
import {
  faSearch,
  faPhone,
  faEnvelope,
  faUser,
  faBars,
  faTimes,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import logoImg from '../assets/NMT _logo.png';

// Enhanced Logo Component for HD display
const LogoComponent = ({ className = "logo-img" }) => {
  return (
    <img 
      src={logoImg}
      alt="NMT India Holidays - Premium Travel Services" 
      className={className}
      loading="lazy"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = logoImg; // Fallback to original
      }}
    />
  );
};

const Navbar = () => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMenuOpen(false);
        setMobileDropdownOpen(null);
        document.body.style.overflow = 'auto';
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMenuOpen(false);
    setMobileDropdownOpen(null);
    setSearchOpen(false);
    document.body.style.overflow = 'auto';
  }, [location.pathname]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
    if (searchOpen) setSearchOpen(false);
    setMobileDropdownOpen(null);
    document.body.style.overflow = !menuOpen ? 'hidden' : 'auto';
  };

  const toggleSearch = () => {
    setSearchOpen(!searchOpen);
    if (menuOpen) setMenuOpen(false);
  };

  const toggleMobileDropdown = (index) => {
    setMobileDropdownOpen(mobileDropdownOpen === index ? null : index);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // You can implement actual search functionality here
      console.log(`Searching for: ${searchQuery}`);
      alert(`Searching for: ${searchQuery}`);
      setSearchQuery('');
      setSearchOpen(false);
    }
  };

  const isTourActive = () => {
    return location.pathname.startsWith('/domestic-tours') || 
           location.pathname.startsWith('/international-tours');
  };

  const handleMobileLinkClick = () => {
    setMenuOpen(false);
    setMobileDropdownOpen(null);
    document.body.style.overflow = 'auto';
  };

  const handleOutsideClick = (e) => {
    if (searchOpen && !e.target.closest('.search-wrapper')) {
      setSearchOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [searchOpen]);

  return (
    <>
      <style>{`
        :root {
          --primary-color: #2193b0;
          --secondary-color: #6dd5ed;
          --accent-color: #ff6b6b;
          --text-dark: #080808;
          --text-light: #ffffff;
          --bg-light: #ffffff;
          --border-radius: 8px;
          --box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          --transition: all 0.3s ease;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          font-family: 'Poppins', sans-serif;
        }

        /* Top Header Styles */
        .top-header {
          background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
          padding: 12px 5%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: var(--text-light);
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          z-index: 1001;
          min-height: 50px;
        }

        .top-left {
          display: flex;
          align-items: center;
          gap: 20px;
          flex: 1;
        }

        .top-right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex: 1;
        }

        .top-contact {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;
          color: var(--text-light);
          text-decoration: none;
          transition: var(--transition);
          white-space: nowrap;
        }

        .top-contact:hover {
          opacity: 0.8;
        }

        .social-icons {
          display: flex;
          gap: 12px;
        }

        .social-icon {
          color: var(--text-light);
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.3);
          transition: var(--transition);
          text-decoration: none;
        }

        .social-icon:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: translateY(-2px);
        }

        /* Main Navbar Styles */
        .navbar {
          position: sticky;
          top: 0;
          z-index: 1000;
          background: var(--bg-light);
          padding: 0 5%;
          transition: var(--transition);
          border-bottom: 1px solid #e5e7eb;
        }

        .navbar.scrolled {
          box-shadow: var(--box-shadow);
        }

        .navbar-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 85px;
          max-width: 1400px;
          margin: 0 auto;
        }

        /* Enhanced Logo Container for Better Visibility */
        .logo-container {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 8px 12px;
          border-radius: var(--border-radius);
          // background: linear-gradient(135deg, #f8fafc, #ffffff);
          // border: 2px solid rgba(33, 147, 176, 0.1);
          transition: var(--transition);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          min-width: 200px;
          height: 70px;
        }

        .logo-container:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(33, 147, 176, 0.15);
          border-color: rgba(33, 147, 176, 0.3);
        }

        /* Enhanced Logo Styles for Maximum Visibility */
        .logo-img {
          height: 65px;
          width: auto;
          max-width: 250px;
          min-width: 180px;
          transition: var(--transition);
          /* Enhanced rendering for crisp display */
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
          /* Prevent blurry scaling */
          transform: translateZ(0);
          backface-visibility: hidden;
          /* Smooth scaling */
          object-fit: contain;
          object-position: center;
          /* Enhanced contrast and brightness */
          filter: contrast(1.1) brightness(1.05) saturate(1.1);
          /* Better edge definition */
          -webkit-filter: contrast(1.1) brightness(1.05) saturate(1.1);
        }

        .logo-img:hover {
          transform: scale(1.05) translateZ(0);
          filter: contrast(1.2) brightness(1.1) saturate(1.2);
        }

        .nav-main {
          display: flex;
          align-items: center;
          gap: 30px;
        }

        .nav-links {
          display: flex;
          align-items: center;
          gap: 25px;
        }

        .nav-links a, .nav-link {
          font-size: 18px;
          font-weight: 600;
          color: #0a0808ff;
          text-decoration: none;
          position: relative;
          padding: 8px 0;
          transition: var(--transition);
          cursor: pointer;
        }

        .nav-links a:hover, .nav-link:hover {
          color: var(--primary-color);
        }

        .nav-links a.active, .nav-link.active {
          color: var(--primary-color);
        }

        .nav-links a.active::after, .nav-link.active::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          height: 3px;
          width: 100%;
          background: var(--primary-color);
          border-radius: 2px;
        }

        /* Dropdown Styles */
        .dropdown {
          position: relative;
        }

        .dropdown-content {
          display: none;
          position: absolute;
          top: 100%;
          left: 0;
          background: var(--bg-light);
          box-shadow: var(--box-shadow);
          padding: 15px 0;
          border-radius: var(--border-radius);
          min-width: 220px;
          z-index: 1002;
          border: 1px solid #e5e7eb;
          opacity: 0;
          transform: translateY(-10px);
          transition: all 0.3s ease;
        }

        .dropdown:hover .dropdown-content {
          display: block;
          opacity: 1;
          transform: translateY(0);
        }

        .dropdown-content a {
          display: block;
          padding: 12px 20px;
          font-size: 15px;
          color: #4b5563;
          transition: var(--transition);
          text-decoration: none;
        }

        .dropdown-content a:hover {
          background: #f8fafc;
          color: var(--primary-color);
        }

        .dropdown-content a.active {
          color: var(--primary-color);
          font-weight: 600;
          background: #f0f9ff;
        }

        /* Search Styles */
        .search-wrapper {
          position: relative;
        }

        .search-form {
          display: ${searchOpen ? 'flex' : 'none'};
          position: absolute;
          top: 50px;
          right: 0;
          background: var(--bg-light);
          border-radius: var(--border-radius);
          overflow: hidden;
          box-shadow: var(--box-shadow);
          z-index: 1002;
          border: 1px solid #e5e7eb;
          opacity: ${searchOpen ? '1' : '0'};
          transform: translateY(${searchOpen ? '0' : '-10px'});
          transition: all 0.3s ease;
        }

        .search-input {
          padding: 10px 18px;
          font-size: 14px;
          border: none;
          outline: none;
          width: 220px;
          color: #4b5563;
        }

        .search-input::placeholder {
          color: #9ca3af;
        }

        .search-button {
          background: var(--primary-color);
          color: var(--text-light);
          border: none;
          padding: 0 15px;
          cursor: pointer;
          transition: var(--transition);
        }

        .search-button:hover {
          background: var(--secondary-color);
        }

        .search-icon {
          font-size: 18px;
          color: var(--text-dark);
          cursor: pointer;
          transition: var(--transition);
          padding: 8px;
          border-radius: 4px;
        }

        .search-icon:hover {
          color: var(--primary-color);
          background: #f8fafc;
        }

        /* Login Button */
        .login-btn {
          background: var(--primary-color);
          color: var(--text-light);
          padding: 10px 22px;
          border-radius: var(--border-radius);
          font-weight: 600;
          text-decoration: none;
          font-size: 15px;
          display: flex;
          align-items: center;
          gap: 8px;
          border: 2px solid var(--primary-color);
          transition: var(--transition);
        }

        .login-btn:hover {
          background: transparent;
          color: var(--primary-color);
          transform: translateY(-2px);
        }

        .login-btn.active {
          background: var(--secondary-color);
          border-color: var(--secondary-color);
        }

        /* Mobile Toggle Button */
        .mobile-toggle {
          display: none;
          color: var(--text-dark);
          font-size: 24px;
          cursor: pointer;
          z-index: 1002;
          background: transparent;
          border: none;
          padding: 8px;
          border-radius: var(--border-radius);
          transition: var(--transition);
        }

        .mobile-toggle:hover {
          background: #f8fafc;
          color: var(--primary-color);
        }

        /* Mobile Navigation Overlay */
        .mobile-overlay {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100vh;
          background: rgba(0, 0, 0, 0.6);
          z-index: 1000;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s ease;
          backdrop-filter: blur(2px);
        }

        .mobile-overlay.active {
          display: block;
          opacity: 1;
          visibility: visible;
        }

        /* Mobile Navigation Panel */
        .mobile-nav {
          position: fixed;
          top: 0;
          left: -100%;
          width: 85%;
          max-width: 350px;
          height: 100vh;
          background: #ffffff;
          padding: 20px;
          z-index: 1001;
          overflow-y: auto;
          transition: left 0.4s cubic-bezier(0.23, 1, 0.32, 1);
          box-shadow: 5px 0 25px rgba(0, 0, 0, 0.3);
        }

        .mobile-nav.open {
          left: 0;
        }

        /* Mobile Nav Header */
        .mobile-nav-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 25px;
          padding-bottom: 15px;
          border-bottom: 1px solid #e5e7eb;
        }

        /* Enhanced Mobile Logo Container */
        .mobile-logo-container {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 6px 10px;
          border-radius: var(--border-radius);
          // background: linear-gradient(135deg, #f8fafc, #ffffff);
          border: 2px solid rgba(33, 147, 176, 0.1);
          transition: var(--transition);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
          min-width: 150px;
          height: 55px;
        }

        .mobile-logo-container:hover {
          transform: translateY(-1px);
          box-shadow: 0 3px 12px rgba(33, 147, 176, 0.15);
          border-color: rgba(33, 147, 176, 0.3);
        }

        /* Mobile logo optimizations */
        .mobile-logo {
          height: 50px;
          width: auto;
          max-width: 180px;
          min-width: 130px;
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
          object-fit: contain;
          object-position: center;
          filter: contrast(1.1) brightness(1.05) saturate(1.1);
          -webkit-filter: contrast(1.1) brightness(1.05) saturate(1.1);
          transition: var(--transition);
        }

        .mobile-logo:hover {
          filter: contrast(1.2) brightness(1.1) saturate(1.2);
        }

        .close-mobile-menu {
          color: var(--text-dark);
          font-size: 22px;
          cursor: pointer;
          background: transparent;
          border: none;
          padding: 8px;
          border-radius: var(--border-radius);
          transition: var(--transition);
        }

        .close-mobile-menu:hover {
          background: #f8fafc;
          color: var(--accent-color);
        }

        /* Mobile Nav Links */
        .mobile-nav-links {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .mobile-nav-link {
          color: var(--text-dark);
          font-size: 16px;
          padding: 12px 15px;
          border-radius: var(--border-radius);
          text-decoration: none;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
          transition: var(--transition);
        }

        .mobile-nav-link:hover {
          background: #f8fafc;
          color: var(--primary-color);
        }

        .mobile-nav-link.active {
          background: #e6f3ff;
          color: var(--primary-color);
          font-weight: 600;
        }

        .mobile-nav-link.active::after {
          content: '';
          position: absolute;
          left: 15px;
          bottom: 0;
          height: 3px;
          width: calc(100% - 30px);
          background: var(--primary-color);
          border-radius: 2px;
        }

        .mobile-dropdown-btn {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 15px;
          background: transparent;
          border: none;
          color: var(--text-dark);
          font-size: 16px;
          cursor: pointer;
          border-radius: var(--border-radius);
          position: relative;
          transition: var(--transition);
          text-align: left;
        }

        .mobile-dropdown-btn:hover {
          background: #f8fafc;
          color: var(--primary-color);
        }

        .mobile-dropdown-btn.active {
          background: #e6f3ff;
          color: var(--primary-color);
          font-weight: 600;
        }

        .mobile-dropdown-btn.active::after {
          content: '';
          position: absolute;
          left: 15px;
          bottom: 0;
          height: 3px;
          width: calc(100% - 30px);
          background: var(--primary-color);
          border-radius: 2px;
        }

        .mobile-dropdown-icon {
          transition: transform 0.3s ease;
          font-size: 14px;
        }

        .mobile-dropdown-icon.open {
          transform: rotate(180deg);
        }

        /* Mobile Dropdown Content */
        .mobile-dropdown-content {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.4s cubic-bezier(0.23, 1, 0.32, 1);
          padding-left: 15px;
        }

        .mobile-dropdown-content.open {
          max-height: 300px;
        }

        .mobile-dropdown-link {
          display: block;
          padding: 10px 15px;
          color: #6b7280;
          font-size: 14px;
          text-decoration: none;
          position: relative;
          border-radius: var(--border-radius);
          transition: var(--transition);
        }

        .mobile-dropdown-link:hover {
          background: #f8fafc;
          color: var(--primary-color);
        }

        .mobile-dropdown-link.active {
          font-weight: 600;
          background: #e6f3ff;
          color: var(--primary-color);
        }

        .mobile-dropdown-link.active::after {
          content: '';
          position: absolute;
          left: 15px;
          bottom: 0;
          height: 2px;
          width: calc(100% - 30px);
          background: var(--primary-color);
          border-radius: 1px;
        }

        /* Mobile Search */
        .mobile-search {
          margin: 20px 0;
          display: flex;
          border-radius: var(--border-radius);
          overflow: hidden;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
        }

        .mobile-search-input {
          flex: 1;
          padding: 12px 15px;
          border: none;
          outline: none;
          font-size: 14px;
          color: var(--text-dark);
          background: transparent;
        }

        .mobile-search-input::placeholder {
          color: #9ca3af;
        }

        .mobile-search-button {
          background: var(--primary-color);
          color: var(--text-light);
          border: none;
          padding: 0 16px;
          cursor: pointer;
          transition: var(--transition);
        }

        .mobile-search-button:hover {
          background: var(--secondary-color);
        }

        /* Mobile Login Button */
        .mobile-login-btn {
          display: block;
          margin-top: 20px;
          text-align: center;
          background: var(--primary-color);
          color: var(--text-light);
          padding: 12px;
          border-radius: var(--border-radius);
          font-weight: 600;
          text-decoration: none;
          font-size: 15px;
          transition: var(--transition);
        }

        .mobile-login-btn:hover {
          background: var(--secondary-color);
          transform: translateY(-2px);
        }

        .mobile-login-btn.active {
          background: var(--secondary-color);
        }

        /* Responsive Styles */
        @media (min-width: 1400px) {
          .logo-img {
            height: 70px;
            max-width: 280px;
            min-width: 200px;
          }
          
          .logo-container {
            min-width: 220px;
            height: 75px;
          }
          
          .navbar-container {
            height: 90px;
          }
        }

        @media (max-width: 1024px) {
          .top-header {
            padding: 10px 4%;
          }
          
          .navbar {
            padding: 0 4%;
          }
          
          .nav-links {
            gap: 20px;
          }
          
          .nav-main {
            gap: 25px;
          }
          
          .logo-img {
            height: 60px;
            max-width: 220px;
            min-width: 170px;
          }
          
          .logo-container {
            min-width: 190px;
            height: 65px;
          }
        }

        @media (max-width: 992px) {
          .nav-links {
            gap: 18px;
          }
          
          .nav-links a, .nav-link {
            font-size: 15px;
          }
          
          .top-contact {
            font-size: 13px;
          }
          
          .social-icon {
            width: 30px;
            height: 30px;
            font-size: 14px;
          }
          
          .logo-img {
            height: 58px;
            max-width: 200px;
            min-width: 160px;
          }
          
          .logo-container {
            min-width: 180px;
            height: 62px;
          }
        }

        @media (max-width: 768px) {
          .top-header {
            padding: 10px 3%;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            min-height: 45px;
          }
          
          .top-left {
            flex: 1;
            justify-content: flex-start;
            gap: 15px;
          }
          
          .top-right {
            flex: 0;
            justify-content: flex-end;
          }
          
          .top-contact {
            font-size: 12px;
            gap: 6px;
          }
          
          .top-contact span {
            display: none;
          }
          
          .social-icons {
            gap: 8px;
          }
          
          .social-icon {
            width: 28px;
            height: 28px;
            font-size: 12px;
          }
          
          .navbar {
            padding: 0 3%;
          }
          
          .nav-main {
            display: none;
          }
          
          .mobile-toggle {
            display: block;
          }
          
          .logo-img {
            height: 55px;
            max-width: 190px;
            min-width: 150px;
          }

          .logo-container {
            min-width: 170px;
            height: 60px;
            padding: 6px 10px;
          }

          .navbar-container {
            height: 75px;
          }
        }

        @media (max-width: 576px) {
          .top-header {
            padding: 8px 3%;
            min-height: 40px;
          }
          
          .top-left {
            gap: 12px;
          }
          
          .top-contact {
            font-size: 11px;
            gap: 4px;
          }
          
          .social-icons {
            gap: 6px;
          }
          
          .social-icon {
            width: 26px;
            height: 26px;
            font-size: 11px;
          }
          
          .logo-img {
            height: 50px;
            max-width: 170px;
            min-width: 140px;
          }
          
          .logo-container {
            min-width: 160px;
            height: 55px;
            padding: 5px 8px;
          }
          
          .navbar-container {
            height: 70px;
          }
          
          .mobile-nav {
            width: 80%;
            max-width: 320px;
          }
          
          .mobile-logo {
            height: 45px;
            max-width: 160px;
            min-width: 120px;
          }
          
          .mobile-logo-container {
            min-width: 140px;
            height: 50px;
          }
        }

        @media (max-width: 480px) {
          .top-header {
            padding: 6px 2%;
            min-height: 38px;
          }
          
          .top-left {
            gap: 10px;
          }
          
          .top-contact {
            font-size: 10px;
            gap: 3px;
          }
          
          .social-icons {
            gap: 5px;
          }
          
          .social-icon {
            width: 24px;
            height: 24px;
            font-size: 10px;
          }
          
          .navbar {
            padding: 0 2%;
          }
          
          .logo-img {
            height: 48px;
            max-width: 160px;
            min-width: 130px;
          }
          
          .logo-container {
            min-width: 150px;
            height: 52px;
            padding: 4px 6px;
          }
          
          .mobile-nav {
            width: 85%;
            max-width: 300px;
          }
          
          .mobile-logo {
            height: 42px;
            max-width: 150px;
            min-width: 110px;
          }
          
          .mobile-logo-container {
            min-width: 130px;
            height: 47px;
          }
        }

        @media (max-width: 360px) {
          .top-header {
            padding: 5px 2%;
            min-height: 36px;
          }
          
          .top-contact {
            font-size: 9px;
          }
          
          .social-icon {
            width: 22px;
            height: 22px;
            font-size: 9px;
          }
          
          .logo-img {
            height: 45px;
            max-width: 150px;
            min-width: 120px;
          }
          
          .logo-container {
            min-width: 140px;
            height: 50px;
          }
          
          .mobile-nav {
            width: 90%;
            max-width: 280px;
          }
          
          .mobile-logo {
            height: 40px;
            max-width: 140px;
            min-width: 100px;
          }
          
          .mobile-logo-container {
            min-width: 120px;
            height: 45px;
          }
        }

        /* High-DPI displays optimization */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          .logo-img, .mobile-logo {
            /* Ensure crisp rendering on retina displays */
            image-rendering: -webkit-optimize-contrast;
            image-rendering: optimizeQuality;
          }
        }

        /* Smooth scrolling for mobile menu */
        .mobile-nav::-webkit-scrollbar {
          width: 6px;
        }

        .mobile-nav::-webkit-scrollbar-track {
          background: #f3f4f6;
          border-radius: 3px;
        }

        .mobile-nav::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }

        .mobile-nav::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>

      {/* Top Header */}
      <div className="top-header">
        <div className="top-left">
          <a href="tel:+919697988891" className="top-contact">
            <FontAwesomeIcon icon={faPhone} /> <span>+91 9697988891</span>
          </a>
          <a href="mailto:info@nmtindiaholidays.com" className="top-contact">
            <FontAwesomeIcon icon={faEnvelope} /> <span>info@nmtindiaholidays.com</span>
          </a>
        </div>
        <div className="top-right">
          <div className="social-icons">
            <a href="https://www.facebook.com/nmtindiaSangli?mibextid=ZbWKwL" className="social-icon" target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon icon={faFacebookF} />
            </a>
            <a href="https://www.instagram.com/nmt_india_holidays?igsh=eDU3Z3J5dm9jN3E4" className="social-icon" target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon icon={faInstagram} />
            </a>
            <a href="#" className="social-icon" target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon icon={faYoutube} />
            </a>
            <a href="https://x.com/nmtindia_tours?t=5mvTEU_wMQ60xKHFAnFk-A&s=09" className="social-icon" target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon icon={faTwitter} />
            </a>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="navbar-container">
          <Link to="/" className="logo-container">
            <LogoComponent />
          </Link>
          
          <button className="mobile-toggle" onClick={toggleMenu} aria-label="Toggle menu">
            <FontAwesomeIcon icon={menuOpen ? faTimes : faBars} />
          </button>

          <div className="nav-main">
            <div className="nav-links">
              <NavLink to="/" end>
                Home
              </NavLink>
              
              <div className="dropdown">
                <span className={`nav-link ${isTourActive() ? 'active' : ''}`}>
                  Tour Packages
                </span>
                <div className="dropdown-content">
                  <NavLink to="/domestic-tours">
                    Domestic Tours
                  </NavLink>
                  <NavLink to="/international-tours">
                    International Tours
                  </NavLink>
                </div>
              </div>
              
              <NavLink to="/aboutus" end>
                About Us
              </NavLink>
              
              <NavLink to="/gallery" end>
                Gallery
              </NavLink>
              
              <NavLink to="/contact-us" end>
                Contact Us
              </NavLink>
            </div>

            <div className="search-wrapper">
              <form className="search-form" onSubmit={handleSearch}>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search destinations..."
                  value={searchQuery} 
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button type="submit" className="search-button" aria-label="Search">
                  <FontAwesomeIcon icon={faSearch} />
                </button>
              </form>
              <FontAwesomeIcon 
                icon={faSearch} 
                className="search-icon" 
                onClick={toggleSearch} 
                aria-label="Open search"
              />
            </div>

            <NavLink to="/user-login" className="login-btn" end>
              <FontAwesomeIcon icon={faUser} /> Login
            </NavLink>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Overlay */}
      <div 
        className={`mobile-overlay ${menuOpen ? 'active' : ''}`} 
        onClick={toggleMenu}
      />

      {/* Mobile Navigation Panel */}
      <div className={`mobile-nav ${menuOpen ? 'open' : ''}`}>
        <div className="mobile-nav-header">
          <Link to="/" onClick={handleMobileLinkClick} className="mobile-logo-container">
            <LogoComponent className="mobile-logo" />
          </Link>
          <button className="close-mobile-menu" onClick={toggleMenu} aria-label="Close menu">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="mobile-nav-links">
          <NavLink 
            to="/" 
            className="mobile-nav-link"
            onClick={handleMobileLinkClick}
            end
          >
            Home
          </NavLink>
          
          <button 
            className={`mobile-dropdown-btn ${isTourActive() ? 'active' : ''}`}
            onClick={() => toggleMobileDropdown(0)}
          >
            Tour Packages
            <FontAwesomeIcon 
              icon={faChevronDown} 
              className={`mobile-dropdown-icon ${mobileDropdownOpen === 0 ? 'open' : ''}`} 
            />
          </button>
          
          <div className={`mobile-dropdown-content ${mobileDropdownOpen === 0 ? 'open' : ''}`}>
            <NavLink 
              to="/domestic-tours" 
              className="mobile-dropdown-link"
              onClick={handleMobileLinkClick}
            >
              Domestic Tours
            </NavLink>
            <NavLink 
              to="/international-tours" 
              className="mobile-dropdown-link"
              onClick={handleMobileLinkClick}
            >
              International Tours
            </NavLink>
          </div>
          
          <NavLink 
            to="/aboutus" 
            className="mobile-nav-link"
            onClick={handleMobileLinkClick}
            end
          >
            About Us
          </NavLink>
          
          <NavLink 
            to="/gallery" 
            className="mobile-nav-link"
            onClick={handleMobileLinkClick}
            end
          >
            Gallery
          </NavLink>
          
          <NavLink 
            to="/contact-us" 
            className="mobile-nav-link"
            onClick={handleMobileLinkClick}
            end
          >
            Contact Us
          </NavLink>
        </div>

        <form className="mobile-search" onSubmit={handleSearch}>
          <input
            type="text"
            className="mobile-search-input"
            placeholder="Search destinations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit" className="mobile-search-button" aria-label="Search">
            <FontAwesomeIcon icon={faSearch} />
          </button>
        </form>

        <NavLink 
          to="/user-login" 
          className="mobile-login-btn"
          onClick={handleMobileLinkClick}
          end
        >
          <FontAwesomeIcon icon={faUser} /> Login
        </NavLink>
      </div>
    </>
  );
};

export default Navbar;
